import { type fabric } from 'fabric';
import type FrameMemberBehaviors from '../objects/FrameMemberBehaviors';
import type SimpleGroup from '../fabric/SimpleGroup';
import type Canvas from '../objects/Canvas';
import { isSimpleGroup } from '../objects/guards';
import { overlappingObjects } from './geometry';

type Sortable = Pick<fabric.Object, 'id' | 'z' | 'connector' | 'type'> &
  Partial<Pick<FrameMemberBehaviors, 'container'>>;

// Layers can be considered as separate Z spaces. Objects in layer 1 are behind
// objects in layer 2. This is used to ensure that comments always appear above
// other elements. Within each layer the Z values are sorted independently.
function layer(obj: Sortable) {
  if (obj.type === 'comment') {
    return 2;
  } else {
    return 1;
  }
}

// [z, id] pairs in hierarchical order, e.g. [[grandparentZ, grandparentId], [parentZ, parentId], [z, id]]
type SortKey = [number, string][];

const getParentId = (obj: Sortable) =>
  obj.connector?.id ?? obj.container ?? undefined;

// Make sure the collection is sorted. We also apply constraints to ensure
// the selection and rendering order is as expected:
//
//  * Frame/table content sits just above the frame itself.
//  * Attached labels are in front of their connector.
//
function zManagerSortCollection(collection: Sortable[]) {
  // first pass finds the z and parent values we need for each object, keyed by id for fast lookup
  const map = new Map<string, { z: number; parentId: string | undefined }>();
  for (const obj of collection) {
    map.set(obj.id, {
      z: obj.z ?? 0,
      parentId: getParentId(obj),
    });
  }

  const keyCache = new Map<string, SortKey>();

  // get the keys for an object, cached to avoid recalculating. Used to
  // retrieve parent keys
  const getKey = (id: string, depth = 0): SortKey => {
    if (keyCache.has(id)) {
      return keyCache.get(id)!;
    }
    const obj = map.get(id);

    if (!obj) {
      return [];
    }

    depth++;
    const key: SortKey = [
      ...(obj.parentId && depth < 10 ? getKey(obj.parentId, depth) ?? [] : []),
      [obj.z, id],
    ];
    keyCache.set(id, key);
    return key;
  };

  // get max z from an array of [z, id] pairs
  const getMaxZ = (key: SortKey) => {
    return key.reduce((acc, [z]) => Math.max(acc, z ?? -Infinity), -Infinity);
  };

  collection.sort((a, b) => {
    // things with no ids (e.g. guides) always go on top
    if (a.id === undefined) return 1;
    if (b.id === undefined) return -1;

    // sort distinct layers
    const layerCmp = layer(a) - layer(b);
    if (layerCmp !== 0) return layerCmp;

    // sort z values, grouped by parent
    const aKey = getKey(a.id);
    const bKey = getKey(b.id);
    for (let i = 0; i < Math.max(aKey.length, bKey.length); i++) {
      // sort parent before its children, but parent siblings above
      if (aKey[i] === undefined) return -1;
      if (bKey[i] === undefined) return 1;

      // at each level, we take the max z of the descendants - this allows an element to be
      // brought forward from it's parent's context, but not sent behind it
      const aZ = getMaxZ(aKey.slice(i));
      const bZ = getMaxZ(bKey.slice(i));
      if (aZ !== bZ) {
        return aZ - bZ;
      }

      // if z is the same, compare ids
      if (aKey[i][1] !== bKey[i][1]) {
        return aKey[i][1].localeCompare(bKey[i][1]);
      }
    }

    // final fallback comparison to ensure determinism, but we should never get to here in practice
    return a.id?.localeCompare(b.id) ?? 0;
  });
}

const Z_INCREMENT = 10;

const getSanitisedZ = (obj: fabric.Object) => (isNaN(obj.z) ? 0 : obj.z ?? 0);
const getMinZ = (objs: fabric.Object[]) =>
  Math.min(0, ...objs.map(getSanitisedZ));
const getMaxZ = (objs: fabric.Object[]) =>
  Math.max(0, ...objs.map(getSanitisedZ));

// returns the thing that will render these objects - either a group or the canvas
// assumes all objects passed are in the same context
const getRenderParent = (obj: fabric.Object): SimpleGroup | Canvas => {
  if (obj.simpleGroup && isSimpleGroup(obj.simpleGroup)) {
    return obj.simpleGroup;
  }
  return obj.canvas;
};

// gets the collection of objects that make up this object's stacking context, e.g. frame siblings
const getZStackingContext = (object: fabric.Object) => {
  const parent = getRenderParent(object);
  return {
    parent,
    context: parent._objects,
  };
};

export type MoveOptions =
  | { targetZ: number; where: 'after' | 'before' }
  | 'bottom'
  | 'top';

const parseMoveOptions = (objects: fabric.Object[], opts: MoveOptions) => {
  if (opts === 'bottom') {
    return { where: 'before', targetZ: getMinZ(objects) };
  } else if (opts === 'top') {
    return { where: 'after', targetZ: getMaxZ(objects) };
  }
  return { where: opts.where, targetZ: opts.targetZ ?? 0 };
};

// Move the object to the specified place in the collection, adjusting the
// z values of other objects if necessary
function zManagerMove(objects: fabric.Object[], opts: MoveOptions) {
  const { context, parent } = getZStackingContext(objects[0]);
  const { targetZ, where } = parseMoveOptions(parent._objects, opts);

  // find any affected siblings - e.g. if we are moving "after", then we need to check for
  // z collisions with subsequent siblings, and vice versa for before
  // we exclude the objects we're moving after/before as they don't need to change
  const siblings = context.filter(
    obj =>
      !objects.includes(obj) &&
      (where === 'after' ? obj.z > targetZ : obj.z < targetZ)
  );

  zManagerSortCollection(siblings);
  if (where === 'before') {
    siblings.reverse();
  }

  // evenly space between this object and the next, defaulting to Z_INCREMENT if there is nothing next
  const rawIncrement = siblings.length
    ? Math.max(
        1,
        Math.floor(Math.abs((siblings[0].z - targetZ) / (objects.length + 1)))
      )
    : Z_INCREMENT;

  // iterate in reverse if we're inserting before
  const increment = (where === 'after' ? 1 : -1) * rawIncrement;
  zManagerSortCollection(objects);
  const objectsToMove = where === 'after' ? objects : objects.toReversed();

  // apply the new z index to each object in turn
  let currentZ = targetZ;
  for (const obj of objectsToMove) {
    currentZ += increment;
    obj.set('z', currentZ);
  }

  // then check if we need to bump any subsequent siblings
  for (const obj of siblings) {
    currentZ += where === 'after' ? 1 : -1;
    if (
      (where === 'after' && obj.z < currentZ) ||
      (where === 'before' && obj.z > currentZ)
    ) {
      obj.set('z', currentZ);
    }
  }

  parent.collectionNeedsSort = true;
}

/** move an object forward or backward in the Z stack by one layer
 *  note this only works on single objects
 */
function zMoveLayer(obj: fabric.Object, direction: 'forward' | 'backward') {
  const { context } = getZStackingContext(obj);
  const zs = overlappingObjects(context, obj)
    .map(getSanitisedZ)
    .filter(z => (direction === 'forward' ? z > obj.z : z < obj.z));
  if (zs.length) {
    const targetZ = direction === 'forward' ? Math.min(...zs) : Math.max(...zs);
    zManagerMove([obj], {
      where: direction === 'forward' ? 'after' : 'before',
      targetZ,
    });
  }
}

export {
  zManagerMove,
  zMoveLayer,
  zManagerSortCollection,
  getMaxZ,
  Z_INCREMENT,
};
